import type { PartialLeavePolicy } from '../lib/partial-leave-time';
import { apiClient } from './client';

/**
 * 근무제와 지각 기준 — 근태 2단계 (2026-08-24).
 *
 * 서버: `apps/chat-server/src/modules/hr/work-schedule.routes.ts`.
 * 설계: `docs/attendance-round2-sample.html` 제안 2 · 화면 D·E.
 *
 * 판정 사다리는 **개인 배정 → 팀 → 회사 기본 → 코드 09:00** 이고, 화면은 그 결과와 **출처**를
 * 함께 받는다 — 사람마다 다른 값으로 판정하면서 그 값을 안 보여 주면 지각 판정을 신뢰할 수 없다.
 */

export type WorkScheduleType = 'STANDARD' | 'FLEX' | 'SELECT' | 'ELASTIC';
export type AssignmentScope = 'USER' | 'TEAM' | 'INSTITUTION';
export type StartSource = 'CHOICE' | 'FLEX_WINDOW_END' | 'CORE' | 'SCHEDULE' | 'DEFAULT';

export const WORK_TYPE_LABEL: Record<string, string> = {
  STANDARD: '통상근무',
  FLEX: '시차출퇴근',
  SELECT: '선택근로',
  ELASTIC: '탄력근로',
  // 교대 (2026-08-30 · 판 K) — 한 줄이 **조(組)** 하나다(주간·야간·휴무).
  SHIFT: '교대근무',
};

/** 기준 시각이 어디서 왔는지 — 화면이 늘 밝히는 문구. */
export const START_SOURCE_LABEL: Record<StartSource, string> = {
  CHOICE: '오늘 내가 고른 시각',
  FLEX_WINDOW_END: '안 고른 날이라 선택폭의 마지막 시각',
  CORE: '선택근로의 코어타임 시작',
  SCHEDULE: '내게 적용된 근무제의 시업 시각',
  DEFAULT: '회사 기본값',
};

export interface WorkScheduleDto {
  id: string;
  name: string;
  type: string;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  workMinutes: number;
  graceMinutes: number;
  coreStart: string | null;
  coreEnd: string | null;
  flexStart: string | null;
  flexEnd: string | null;
  isDefault: boolean;
  /**
   * 근무요일 — **월~일 일곱 자리**, `'1'` 이 근무일 (판 2 · 2026-09-11).
   *
   * ★ 옛 서버는 이 칸을 안 준다(`undefined`). 그때는 월~금으로 본다 — 종전 동작과 같다.
   */
  workdays?: string;
  isActive: boolean;
  sortOrder: number;
  assignedCount: number;
}

export interface WorkAssignmentDto {
  id: string;
  scope: AssignmentScope;
  scheduleId: string;
  userId: string | null;
  userName: string | null;
  teamId: string | null;
  teamName: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export interface WorkPolicyDto {
  date: string;
  /** 그날의 기준 출근 시각. */
  baseStart: string;
  /** 그날의 종업 시각 — 시업이 움직이면 함께 움직인다. */
  baseEnd: string;
  startSource: StartSource;
  source: AssignmentScope | 'DEFAULT';
  graceMinutes: number;
  /** 완전선택근로제면 false — 지각 판정 자체를 하지 않는다. */
  lateJudged: boolean;
  canChooseStart: boolean;
  flexStart: string | null;
  flexEnd: string | null;
  /** 그날 이미 고른 시각. */
  choice: string | null;
  /** 아직 못 하는 것(탄력근로 주 배정표 등)을 밝히는 꼬리표. */
  notice: string | null;
  schedule: { id: string; name: string; type: string } | null;
  /**
   * 반차 · 반반차 «시작 시각» 재료 (2026-09-16 · S6 서버 계약 §4 · §9). **옛 서버는 이 키가 없다** — 그것이 옛 서버 판별이다.
   * ★ `available=false` 여도 시각 · 길이 값은 채워 온다(표시용) · 신청은 422 `WORK_POLICY_UNAVAILABLE`.
   */
  partialLeave?: PartialLeavePolicy | null;
}

/** 관리 — 근무제 목록 + 배정 현황 + 팀 목록. */
export async function listWorkSchedules(): Promise<{
  schedules: WorkScheduleDto[]; assignments: WorkAssignmentDto[]; teams: Array<{ id: string; name: string }>;
}> {
  const { data } = await apiClient.get('/hr/work-schedules');
  return data;
}

export interface WorkScheduleInput {
  name: string; type: string; startTime: string; endTime: string;
  breakMinutes?: number; workMinutes?: number; graceMinutes?: number;
  coreStart?: string | null; coreEnd?: string | null;
  flexStart?: string | null; flexEnd?: string | null;
  isDefault?: boolean; isActive?: boolean; sortOrder?: number;
  /** 근무요일 — 월~일 일곱 자리(판 2). 안 보내면 서버가 월~금으로 둔다. */
  workdays?: string;
}

/** 근무요일 기본 — **월~금**. 종전에 코드에 박혀 있던 「토·일은 쉰다」와 같은 답이다. */
export const DEFAULT_WORKDAYS = '1111100';

/** 자리 순서대로 사람이 읽는 이름. **월요일이 첫 자리다.** */
export const WEEKDAY_LABELS = ['월', '화', '수', '목', '금', '토', '일'] as const;

/** 하루 이상 골랐는가 — 하나도 없는 근무제는 그 사람에게 근무일이 영영 없다. */
export function workdaysValid(mask: string): boolean {
  return /^[01]{7}$/.test(mask) && mask.includes('1');
}

/** `'1111100'` → `'월·화·수·목·금'`. 목록에서 한눈에 읽히게. */
export function workdaysText(mask: string | null | undefined): string {
  const m = typeof mask === 'string' && /^[01]{7}$/.test(mask) ? mask : DEFAULT_WORKDAYS;
  const picked = WEEKDAY_LABELS.filter((_, i) => m[i] === '1');
  return picked.length > 0 ? picked.join('·') : '없음';
}

export async function createWorkSchedule(body: WorkScheduleInput): Promise<{ id: string }> {
  const { data } = await apiClient.post('/hr/work-schedules', body);
  return data;
}

export async function updateWorkSchedule(id: string, body: WorkScheduleInput): Promise<{ id: string }> {
  const { data } = await apiClient.patch(`/hr/work-schedules/${id}`, body);
  return data;
}

/** 관리 — 배정 저장. 같은 대상의 열린 배정을 닫고 새 줄을 쌓는다(지난 판정 보존). */
export async function saveWorkAssignments(items: Array<{
  scope: AssignmentScope; scheduleId: string; userId?: string | null; teamId?: string | null;
  effectiveFrom: string; effectiveTo?: string | null;
}>): Promise<{ saved: number }> {
  const { data } = await apiClient.put('/hr/work-assignments', { items });
  return data;
}

/** 본인 — 그날 내 기준 시각과 출처. */
export async function getWorkPolicy(date?: string): Promise<WorkPolicyDto> {
  const { data } = await apiClient.get<WorkPolicyDto>('/hr/attendance/policy', {
    params: date ? { date } : undefined,
  });
  return data;
}

/** 본인 — 시차출퇴근에서 그날 출근 시각 고르기. 이미 출근했으면 서버가 409 로 막는다. */
export async function chooseDayStart(date: string, startTime: string): Promise<{
  date: string; choice: string; baseStart: string; baseEnd: string;
}> {
  const { data } = await apiClient.post('/hr/attendance/day-choice', { date, startTime });
  return data;
}

// ── 교대 편성 (2026-08-30 · 판 K) ─────────────────────────────────────────

export interface ShiftDto {
  id: string;
  name: string;
  startTime: string | null;
  endTime: string | null;
  workMinutes: number;
  /** 소정 0분 = **휴무 조**. 화면이 다르게 그린다. */
  isOff: boolean;
}

export interface ShiftPersonDto {
  userId: string;
  name: string;
  teamId: string | null;
  teamName: string | null;
  /** `YYYY-MM-DD` → 조 id. 없는 날은 편성이 없는 날이다. */
  days: Record<string, string>;
}

export async function listShifts(from: string, to: string, teamId?: string): Promise<{
  from: string; to: string; shifts: ShiftDto[]; people: ShiftPersonDto[];
}> {
  const { data } = await apiClient.get('/hr/shifts', { params: { from, to, ...(teamId ? { teamId } : {}) } });
  return data;
}

/** 빈 조(`null`)는 **지우기** 다. */
export async function saveShifts(items: Array<{
  userId: string; workDate: string; scheduleId: string | null;
}>): Promise<{ saved: number; cleared: number }> {
  const { data } = await apiClient.put('/hr/shifts', { items });
  return data;
}
